import React, { useMemo, useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import {
    Button,
    Tooltip,
    useTheme,
    Box,
    Switch,
    FormControlLabel,
} from '@material-ui/core'
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab'
import { Container } from '../../../components'

import { useUser, useService, useSchedule } from '../../../hooks'
import { useQueryClient } from '@tanstack/react-query'
import { convertListToMap } from '../../../models'
import { CreateProjectModal } from './modals/CreateProjectModal'
import { WorkspaceProjectsTable } from './WorkspaceProjectsTable'
import { ManageBulkProjectsPanel } from './ManageBulkProjectsPanel'
import { Routes } from '../../../navigation/config/routes'
import { axiosInstance, resolveRoute } from '../../../helpers'
import {
    WorkspaceProjectsAnalytics,
    ProjectAnalytics,
} from '../../../hooks/useEZFolioProject'
import { ProjectAnalyticsTable } from './ProjectAnalyticsTable'
import { ProjectDetailModal } from './modals/ProjectDetailModal'
import { usePortfolioSchedule } from '../../../hooks/usePortfolioSchedule'
import { workorderContext } from '../../../contexts'
import { useUserTags } from '../../../contexts/UserTagsContext'
import GetAppIcon from '@material-ui/icons/GetApp'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import AssessmentIcon from '@material-ui/icons/Assessment'
import VpnKeyIcon from '@material-ui/icons/VpnKey'
import { ProjectUnitDefinitionsModal } from './modals/ProjectUnitDefinitionsModal'
import { ProjectKeyAnalyticsModal } from './modals/ProjectKeyAnalyticsModal'
import { COLUMN_DEFINITIONS_BUTTON_LABEL } from './projectUnitColumnDefinitions'

export const ProjectsTab = () => {
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>([])
    const theme = useTheme()
    const {
        organization,
        rootUser,
        workspaceUser,
        actions: { updateUser },
    } = useUser()
    const queryClient = useQueryClient()

    // Manually invalidate queries when organization changes
    useEffect(() => {
        if (organization) {
            // This will force React Query to refetch data when organization changes
            queryClient.invalidateQueries({
                queryKey: ['workspace-project-analytics'],
                exact: false,
            })
            queryClient.invalidateQueries({
                queryKey: ['project-analytics'],
                exact: false,
            })
            queryClient.invalidateQueries({
                queryKey: ['project-drafts'],
                exact: false,
            })
        }
    }, [organization, queryClient])

    const [showArchived, setShowArchived] = useState(false)
    const [showAnalytics, setShowAnalytics] = useState(false)
    const [showKeyAnalytics, setShowKeyAnalytics] = useState(false)
    const [showBulkManager, setShowBulkManager] = useState(false)
    const [unitDefinitionsOpen, setUnitDefinitionsOpen] = useState(false)
    const [viewMode, setViewMode] = useState<'workspace' | 'project'>(
        'workspace',
    )

    useEffect(() => {
        if (viewMode !== 'project') {
            setShowAnalytics(false)
            setShowKeyAnalytics(false)
            setUnitDefinitionsOpen(false)
        }
        if (viewMode !== 'workspace') {
            setShowBulkManager(false)
        }
    }, [viewMode])
    const [projectAnalytics, setProjectAnalytics] = useState<
        ProjectAnalytics[]
    >([])
    const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<
        number | undefined
    >(undefined)
    const [selectedWorkspaceName, setSelectedWorkspaceName] = useState<
        string | undefined
    >(undefined)
    const [isUpdatingWeeklyReport, setIsUpdatingWeeklyReport] = useState(false)

    const weeklyReportEnabled =
        workspaceUser?.weekly_project_analytics_report ??
        rootUser?.weekly_project_analytics_report ??
        false

    const [formState, setFormState] = useState<WorkspaceProjectFormState>({
        selectedWorkspaceProjectAnalytic: null,
        selectedProjectAnalytic: null,
        modalState: {
            projectDetailOpen: false,
            createProjectOpen: false,
        },
    })

    const { workorderList, loadingState, getWorkorderList } = workorderContext()
    const workoderListLoading = loadingState.getWorkorderList
    const {
        createSchedule,
        updateSchedule,
        syncBedStatuses,
        getUnitReport,
        archiveProject,
    } = useSchedule()

    const {
        portfolioScheduleDetail,
        getPortfolioScheduleDetail,
    } = usePortfolioSchedule()

    const { serviceList } = useService({ getServiceList: true, cleanUp: true })

    const { userTags } = useUserTags()

    const apartmentList = useMemo(() => {
        return (
            rootUser?.workspaces
                .map((workspaceUser) => workspaceUser.active_workspace)
                .filter(
                    (workspace) => workspace.organization === organization,
                ) ?? []
        )
    }, [rootUser, organization])

    const bulkManagerProperties = useMemo(() => {
        let list = apartmentList
        if (selectedTagIds.length > 0) {
            const allowedIds = new Set(
                userTags
                    .filter((tag) => selectedTagIds.includes(tag.id))
                    .flatMap((tag) => tag.apartments),
            )
            list = list.filter((apartment) => allowedIds.has(apartment.id))
        }
        return list.map((apartment) => ({
            id: apartment.id,
            name: apartment.name,
        }))
    }, [apartmentList, selectedTagIds, userTags])

    const apartmentMap = convertListToMap(apartmentList)

    const selectedApartment = useMemo(() => {
        return apartmentMap[formState.selectedWorkspaceProjectAnalytic?.id ?? 0]
    }, [formState.selectedWorkspaceProjectAnalytic, apartmentMap])

    const handleViewModeChange = (
        event: React.MouseEvent<HTMLElement>,
        newViewMode: 'workspace' | 'project' | null,
    ) => {
        if (newViewMode !== null) {
            setViewMode(newViewMode)
        }
    }

    const onClickWorkspaceRow = async (
        workspaceProjectAnalytic: WorkspaceProjectsAnalytics,
    ) => {
        setSelectedWorkspaceName(workspaceProjectAnalytic.apartment_name)
        setSelectedWorkspaceId(workspaceProjectAnalytic.id)
        setViewMode('project')
    }

    const onAddProject = (
        workspaceProjectAnalytic: WorkspaceProjectsAnalytics,
    ) => {
        setFormState({
            ...formState,
            selectedWorkspaceProjectAnalytic: workspaceProjectAnalytic,
            modalState: {
                ...formState.modalState,
                createProjectOpen: true,
            },
        })
    }

    const onArchiveProject = async (projectId: number) => {
        const success = await archiveProject(projectId)
        if (!success) return

        await Promise.all([
            queryClient.invalidateQueries({
                queryKey: ['project-analytics'],
                exact: false,
            }),
            queryClient.invalidateQueries({
                queryKey: ['project-analytics-all'],
                exact: false,
            }),
            queryClient.invalidateQueries({
                queryKey: ['workspace-project-analytics'],
                exact: false,
            }),
        ])
    }

    const navigateToScheduleDetail = (projectAnalytic: ProjectAnalytics) => {
        let route = Routes.apartment.schedule.detail as string
        if (!projectAnalytic.active) {
            route = Routes.apartment.schedule.archived as string
        }
        const params = new URLSearchParams({
            apartment: projectAnalytic.apartment_id.toString() ?? '',
            setup: 'false',
        }).toString()

        const url = `${resolveRoute(
            route,
            ':id',
            projectAnalytic.id,
        )}?${params}`

        window.open(url, '_blank')
    }

    const openProjectDetails = (projectAnalytic: ProjectAnalytics) => {
        setFormState({
            ...formState,
            selectedProjectAnalytic: projectAnalytic,
            modalState: {
                ...formState.modalState,
                projectDetailOpen: true,
            },
        })
        let params = {}
        if (organization) {
            params = {
                organization: organization,
            }
        }
        getPortfolioScheduleDetail(projectAnalytic.id, params)
        getWorkorderList({
            params: {
                schedule: projectAnalytic.id,
                portfolio: true,
            },
        })
    }

    const handleResetWorkspace = () => {
        setSelectedWorkspaceId(undefined)
        setSelectedWorkspaceName(undefined)
    }

    const handleWeeklyReportToggle = async (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        if (!workspaceUser) return

        const enabled = event.target.checked
        setIsUpdatingWeeklyReport(true)

        try {
            await updateUser({
                isCurrentUser: true,
                params: { id: workspaceUser.id },
                body: { weekly_project_analytics_report: enabled },
            })
            toast.success(
                enabled
                    ? 'Weekly project analytics report enabled'
                    : 'Weekly project analytics report disabled',
            )
        } catch {
            toast.error('Failed to update weekly report preference')
        } finally {
            setIsUpdatingWeeklyReport(false)
        }
    }

    const handleGenerateReport = async () => {
        try {
            const response = await axiosInstance.get(
                '/workorder/org-project-analytics/generate-report/',
                {
                    params: {
                        archived: showArchived,
                    },
                },
            )
            if (response.status === 200) {
                toast.success('report generation started')
            } else {
                toast.error('report generation failed')
            }
        } catch (error) {
            toast.error('report generation failed')
        }
    }

    return (
        <>
            <Container
                style={{
                    marginTop: theme.spacing(1),
                    flex: 1,
                    flexDirection: 'column',
                    height: 'calc(100vh - 80px)',
                    minHeight: '600px',
                    display: 'flex',
                }}
            >
                {!showBulkManager && (
                    <Container
                        style={{
                            flexShrink: 0,
                            flexDirection: 'row',
                            padding: theme.spacing(1),
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: theme.spacing(2),
                        }}
                    >
                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={handleViewModeChange}
                            size="small"
                            style={{
                                backgroundColor: theme.palette.background.paper,
                                border: `1px solid ${theme.palette.grey[300]}`,
                                flexShrink: 0,
                            }}
                        >
                            <ToggleButton
                                value="workspace"
                                aria-label="workspace"
                            >
                                Workspaces
                            </ToggleButton>
                            <ToggleButton value="project" aria-label="project">
                                Projects
                            </ToggleButton>
                        </ToggleButtonGroup>

                        <Box
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: theme.spacing(1),
                                flexWrap: 'wrap',
                                justifyContent: 'flex-end',
                            }}
                        >
                            {viewMode === 'project' && (
                                <>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<InfoOutlinedIcon />}
                                        onClick={() =>
                                            setUnitDefinitionsOpen(true)
                                        }
                                    >
                                        {COLUMN_DEFINITIONS_BUTTON_LABEL}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<AssessmentIcon />}
                                        onClick={() =>
                                            setShowAnalytics((prev) => !prev)
                                        }
                                        style={
                                            showAnalytics
                                                ? {
                                                      color:
                                                          theme.palette.primary
                                                              .main,
                                                      borderColor:
                                                          theme.palette.primary
                                                              .main,
                                                  }
                                                : undefined
                                        }
                                    >
                                        View analytics
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<VpnKeyIcon />}
                                        onClick={() =>
                                            setShowKeyAnalytics(true)
                                        }
                                        style={
                                            showKeyAnalytics
                                                ? {
                                                      color:
                                                          theme.palette.primary
                                                              .main,
                                                      borderColor:
                                                          theme.palette.primary
                                                              .main,
                                                  }
                                                : undefined
                                        }
                                    >
                                        View key analytics
                                    </Button>
                                </>
                            )}

                            <Tooltip title="Receive a combined project analytics report by email every Friday across all your organizations">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={weeklyReportEnabled}
                                            onChange={handleWeeklyReportToggle}
                                            color="primary"
                                            disabled={isUpdatingWeeklyReport}
                                        />
                                    }
                                    labelPlacement="start"
                                    label="Weekly report"
                                    style={{ margin: 0 }}
                                />
                            </Tooltip>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<GetAppIcon />}
                                onClick={handleGenerateReport}
                            >
                                Generate report
                            </Button>
                        </Box>
                    </Container>
                )}

                {showBulkManager ? (
                    <ManageBulkProjectsPanel
                        properties={bulkManagerProperties}
                        selectedTagIds={selectedTagIds}
                        onSelectedTagIdsChange={setSelectedTagIds}
                        onClose={() => setShowBulkManager(false)}
                        theme={theme}
                    />
                ) : (
                    <>
                        {/* workspace view mode */}
                        {viewMode === 'workspace' && (
                            <WorkspaceProjectsTable
                                onClickRow={onClickWorkspaceRow}
                                onAddProject={onAddProject}
                                onManageBulkProjects={() =>
                                    setShowBulkManager(true)
                                }
                                organizationId={organization ?? undefined}
                                selectedTagIds={selectedTagIds}
                                onSelectedTagIdsChange={setSelectedTagIds}
                            />
                        )}

                        {/* project view mode */}
                        {viewMode === 'project' && (
                            <ProjectAnalyticsTable
                                workspaceId={selectedWorkspaceId}
                                workspaceName={selectedWorkspaceName}
                                navigateToProject={navigateToScheduleDetail}
                                onArchiveProject={onArchiveProject}
                                onViewProjectDetails={openProjectDetails}
                                onResetWorkspace={handleResetWorkspace}
                                showArchived={showArchived}
                                setShowArchived={setShowArchived}
                                selectedTagIds={selectedTagIds}
                                onSelectedTagIdsChange={setSelectedTagIds}
                                showAnalytics={showAnalytics}
                                setShowAnalytics={setShowAnalytics}
                            />
                        )}
                    </>
                )}
            </Container>

            <ProjectUnitDefinitionsModal
                open={unitDefinitionsOpen}
                onClose={() => setUnitDefinitionsOpen(false)}
            />

            <ProjectKeyAnalyticsModal
                open={showKeyAnalytics}
                onClose={() => setShowKeyAnalytics(false)}
                workspaceId={selectedWorkspaceId}
                organizationId={organization ?? undefined}
                archived={showArchived}
            />

            {/* Create Project Modal */}
            {formState.selectedWorkspaceProjectAnalytic && selectedApartment && (
                <CreateProjectModal
                    open={formState.modalState.createProjectOpen}
                    onClose={() =>
                        setFormState({
                            ...formState,
                            modalState: {
                                ...formState.modalState,
                                createProjectOpen: false,
                            },
                        })
                    }
                    theme={theme}
                    createSchedule={createSchedule}
                    updateSchedule={updateSchedule}
                    apartment={selectedApartment}
                    onCreateProject={() => {
                        toast.success('Project created successfully!')
                    }}
                    syncBedStatuses={syncBedStatuses}
                    getUnitReport={getUnitReport}
                />
            )}

            {/* Project Details Modal */}
            <ProjectDetailModal
                open={formState.modalState.projectDetailOpen}
                onClose={() =>
                    setFormState({
                        ...formState,
                        modalState: {
                            projectDetailOpen: false,
                            createProjectOpen: false,
                        },
                    })
                }
                scheduleDetail={portfolioScheduleDetail}
                workorderList={workorderList ?? []}
                workorderLoading={workoderListLoading}
                serviceList={serviceList}
                projectName={formState.selectedProjectAnalytic?.name ?? ''}
            />
        </>
    )
}

interface WorkspaceProjectFormState {
    selectedWorkspaceProjectAnalytic: WorkspaceProjectsAnalytics | null
    selectedProjectAnalytic: ProjectAnalytics | null
    modalState: {
        projectDetailOpen: boolean
        createProjectOpen: boolean
    }
}
